// src/tabs/logements/LogementWebsiteTab.jsx — Phase 5.2 IA Claude génération contenu (ADR-115)
//
// Onglet "🖥️ Website" activé dès Phase 5.2 (Q-D4 Dimitri validée).
// Permet à l'admin de générer description + meta SEO + ordre photos via Claude,
// preview FR | EN side-by-side, sauvegarder, publier.
//
// Périmètre Phase 5.2 (volontairement réduit, cf. scope orchestrateur) :
//   - Pas de régénération partielle par section (reporté 5.3)
//   - Pas de calendrier Beds24 dynamique (reporté 5.3)
//   - Pas de formulaire direct_booking_requests complet (reporté 5.4)
//
// Sécurité (F-MOY-1 + Q-D11 pessimist) :
//   - Preview React = textContent strict (jamais dangerouslySetInnerHTML)
//   - Description split en paragraphes via \n\n (rendu <p>...</p> safe)
//   - Server-side strip HTML déjà appliqué côté api/sites.js (defense-in-depth)
//
// Workflow utilisateur :
//   1. Saisir slug (auto-suggest depuis nom logement)
//   2. URL Airbnb équivalent (Q-D9 CTA voyageur sans calendrier 5.3)
//   3. Bouton "🤖 Générer contenu IA" → callClaude, preview retourné
//   4. Si hoguet_warning=true → bandeau orange (Q-D7 non-bloquant)
//   5. Bouton "💾 Enregistrer" → site_content_upsert (statut=draft par défaut)
//   6. Bouton "🚀 Publier" → upsert + statut=published + best-effort revalidate

import { useState, useEffect, useMemo } from "react"
import { DS } from "../../lib/ui"
import { Section, Row, Field, FieldFull, Select } from "./atoms"
import { supabase } from "../../supabase"
// WAGON R Lot R3 (a) — Import plateforme migré depuis SyncBeds24Tab.
// fetchListingFromBeds24 (lecture seule), résolution mapping et modale de diff
// non destructive ImportPreviewModal déplacés byte-identiques. L'apply écrit en
// cfg via onUpdateBatch — JAMAIS de push vers Beds24/plateformes (lecture seule).
import { fetchListingFromBeds24 } from "../../lib/beds24-listings"
import { getBeds24Mapping } from "../../lib/beds24-mapping-lookup"
import ImportPreviewModal from "./ImportPreviewModal"
import CopyToChambresButton from "./CopyToChambresButton"
// WAGON R (Lot R4 b) — galerie centrale du logement déplacée depuis InfoFbzTab.
// Composant inchangé (logement_id={log.id}). Position logique : en haut du
// Website (contenu visuel du site public + livret voyageur).
import LogementGalleryEditor from "./LogementGalleryEditor"
// DA GROUPE 3 — Lot L13 (maquette #6). Pictos lucide en remplacement des emojis
// d'équipement + icônes d'en-tête de section. AFFICHAGE SEUL : aucune clé/valeur
// d'équipement, aucun handler, aucun état ni data-testid modifiés.
import {
  Images, Import, Sparkles, Globe, Scale, FileText, Save, RefreshCw, Eye,
  Wifi, Snowflake, Fan, Flame, Laptop, CookingPot, UtensilsCrossed, Microwave,
  Refrigerator, CupSoda, Coffee, Croissant, WashingMachine, Wind, Shirt, Tv,
  MonitorPlay, Waves, Sprout, Trees, SquareParking, Car, Coins, MoveVertical,
  PawPrint, Users, Baby, Armchair, Droplets, AlarmSmoke, ShieldAlert,
  BriefcaseMedical, FireExtinguisher, Package,
} from "lucide-react"

// Slug auto-suggest local (cohérent avec slugifyName côté api/sites.js).
export function slugifyName(name) {
  if (!name) return ''
  return String(name)
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80)
}

export const SLUG_REGEX = /^[a-z0-9][a-z0-9-]{1,79}$/
const SEO_TITLE_MAX = 60
const SEO_DESC_MAX = 160

// ─── Catalogue équipements (WAGON R Lot R1 — move depuis SyncBeds24Tab) ───────
// Bloc déplacé BYTE-IDENTIQUE depuis SyncBeds24Tab (constantes + helpers
// d'héritage + JSX d'édition). Path cfg `cfg.props[].logs[].equipements`
// INCHANGÉ (array de string IDs) ; consommateur réel intouché (api/sites.js
// LOT 4 (2026-08-28) — constantes + helpers EXPORTÉS pour la fiche V2
// (FicheSiteCtrl) : mêmes données, mêmes contrats, zéro duplication.
// prompt IA — STANDARD_EQUIPEMENTS lu côté serveur). Save = onUpdate (cfg),
// JAMAIS site_content_upsert (correction C-2 pessimist).
// NB Lot R1 : doublon temporaire assumé avec SyncBeds24Tab — la suppression du
// bloc côté Sync Beds24 est planifiée Lot R4 (suppression de l'onglet).
export const STANDARD_EQUIPEMENTS = [
  { id: "wifi", label: "📶 WiFi haut débit", group: "Essentiels" },
  { id: "ac", label: "❄️ Climatisation", group: "Essentiels" },
  // WAGON R5 Lot 2 (D1) — « ventilateur » est un équipement DISTINCT de la
  // climatisation (Beds24 FAN_PORTABLE ≠ AIR_CONDITIONING ; ADR-060/ADR-189).
  // Sans cette entrée, l'import `fan_portable → 'fan'` était silencieusement
  // perdu (id 'fan' inexistant côté catalogue) → Dimitri voyait « clim » à la
  // place du ventilateur réellement annoncé sur les plateformes.
  { id: "fan", label: "🌀 Ventilateur", group: "Essentiels" },
  { id: "heating", label: "🔥 Chauffage", group: "Essentiels" },
  { id: "workspace", label: "💻 Espace de travail dédié", group: "Essentiels" },
  { id: "kitchen", label: "🍳 Cuisine équipée", group: "Cuisine" },
  { id: "dishwasher", label: "🍽️ Lave-vaisselle", group: "Cuisine" },
  // WAGON R5 Lot 2 (D3) — équipements Beds24 réels présents sur les rooms FBZ
  // (FAN_PORTABLE traité ci-dessus ; MICROWAVE/OVEN/REFRIGERATOR/KETTLE/TOASTER)
  // mais jusqu'ici importés vers des ids absents du catalogue → invisibles/non
  // cochables. Ajoutés pour que l'admin coche tout ce qui existe sur ses fiches.
  { id: "microwave", label: "📡 Micro-ondes", group: "Cuisine" },
  { id: "oven", label: "🔥 Four", group: "Cuisine" },
  { id: "fridge", label: "🧊 Réfrigérateur", group: "Cuisine" },
  { id: "kettle", label: "🫖 Bouilloire", group: "Cuisine" },
  { id: "toaster", label: "🍞 Grille-pain", group: "Cuisine" },
  { id: "coffee", label: "☕ Machine à café", group: "Cuisine" },
  { id: "breakfast", label: "🥐 Petit déjeuner inclus", group: "Cuisine" },
  { id: "washer", label: "🧺 Lave-linge", group: "Linge" },
  { id: "dryer", label: "🌬️ Sèche-linge", group: "Linge" },
  { id: "iron", label: "👕 Fer & table à repasser", group: "Linge" },
  { id: "tv", label: "📺 TV", group: "Loisirs" },
  { id: "streaming", label: "🎬 TV connectée (Netflix…)", group: "Loisirs" },
  { id: "pool", label: "🏊 Piscine", group: "Extérieur" },
  { id: "balcony", label: "🌿 Balcon / terrasse", group: "Extérieur" },
  { id: "garden", label: "🌳 Jardin", group: "Extérieur" },
  {
    id: "parking_free",
    label: "🅿️ Parking gratuit sur place",
    group: "Stationnement",
  },
  {
    id: "parking_street",
    label: "🚗 Parking gratuit dans la rue",
    group: "Stationnement",
  },
  { id: "parking_paid", label: "💶 Parking payant", group: "Stationnement" },
  { id: "elevator", label: "🛗 Ascenseur", group: "Bâtiment" },
  { id: "pets", label: "🐾 Animaux acceptés", group: "Politique" },
  { id: "family", label: "👨‍👩‍👧 Adapté aux familles", group: "Politique" },
  { id: "crib", label: "👶 Lit bébé sur demande", group: "Famille" },
  { id: "high_chair", label: "🍼 Chaise haute", group: "Famille" },
  { id: "hairdryer", label: "💇 Sèche-cheveux", group: "Salle de bain" },
  { id: "amenities", label: "🧴 Produits d'accueil", group: "Salle de bain" },
  { id: "smoke_detector", label: "🚨 Détecteur fumée", group: "Sécurité" },
  { id: "co_detector", label: "🛑 Détecteur CO", group: "Sécurité" },
  { id: "first_aid", label: "🩹 Trousse premiers secours", group: "Sécurité" },
  { id: "extinguisher", label: "🧯 Extincteur", group: "Sécurité" },
]

export const EQUIPEMENTS_GROUPS = [
  "Essentiels",
  "Cuisine",
  "Linge",
  "Loisirs",
  "Extérieur",
  "Stationnement",
  "Bâtiment",
  "Politique",
  "Famille",
  "Salle de bain",
  "Sécurité",
]

// DA GROUPE 3 — Lot L13 : mapping AFFICHAGE clé d'équipement → picto lucide.
// Les clés/labels de STANDARD_EQUIPEMENTS restent INCHANGÉS (data). Ce mapping
// est purement visuel : il remplace l'emoji du label par un picto. Toute clé
// absente retombe sur un picto générique (EQUIP_ICON_FALLBACK).
const EQUIP_ICONS = {
  wifi: Wifi, ac: Snowflake, fan: Fan, heating: Flame, workspace: Laptop,
  kitchen: CookingPot, dishwasher: UtensilsCrossed, microwave: Microwave,
  oven: Flame, fridge: Refrigerator, kettle: CupSoda, toaster: Package,
  coffee: Coffee, breakfast: Croissant, washer: WashingMachine, dryer: Wind,
  iron: Shirt, tv: Tv, streaming: MonitorPlay, pool: Waves, balcony: Sprout,
  garden: Trees, parking_free: SquareParking, parking_street: Car,
  parking_paid: Coins, elevator: MoveVertical, pets: PawPrint, family: Users,
  crib: Baby, high_chair: Armchair, hairdryer: Wind, amenities: Droplets,
  smoke_detector: AlarmSmoke, co_detector: ShieldAlert,
  first_aid: BriefcaseMedical, extinguisher: FireExtinguisher,
}
const EQUIP_ICON_FALLBACK = Package

// Retire l'emoji de tête d'un label d'équipement pour l'affichage (le picto
// lucide le remplace). Les labels suivent tous le format « <emoji> <texte> » ;
// le texte (« WiFi haut débit », « Climatisation »…) est conservé tel quel.
function equipementLabelText(label) {
  return String(label || "").replace(/^\S+\s+/, "")
}

// En-tête de section DA : pastille d'icône lucide (teinte-catégorie). Rendu comme
// FRAGMENT (picto + texte nu) pour rester un unique nœud texte interrogeable par
// les tests (getByText('Photos du logement')).
function SectionHeading({ icon: Icon, label, bg = DS.catCielBg, color = DS.catCielIco }) {
  return (
    <>
      <span
        aria-hidden="true"
        style={{
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          width: 26, height: 26, borderRadius: 8, background: bg, color,
          marginRight: 9, verticalAlign: "middle", flexShrink: 0,
        }}
      >
        <Icon size={15} strokeWidth={2} />
      </span>
      {label}
    </>
  )
}

// DÉCISION DESIGN « FR = source unique » — mapping champ logique → colonnes.
// Sert au calcul des flags de retouche manuelle (manual_<key>_<lang>) côté UI,
// miroir de lib/site-content-translation.js (TRANSLATABLE_FIELDS).
export const TRANSLATABLE_KEYS = [
  { key: 'description', en: 'description_en', es: 'description_es' },
  { key: 'meta_seo_title', en: 'meta_seo_title_en', es: 'meta_seo_title_es' },
  { key: 'meta_seo_description', en: 'meta_seo_description_en', es: 'meta_seo_description_es' },
]

// Helper appel API avec JWT admin (pattern ContratTab/FactureTab).
export async function callApi(path, body) {
  const { data: { session } } = await supabase.auth.getSession()
  const token = session?.access_token
  if (!token) throw new Error("Pas de session admin active. Reconnectez-vous.")
  const r = await fetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
    body: JSON.stringify(body),
  })
  const json = await r.json().catch(() => ({ ok: false, error: 'invalid_json_response' }))
  return { ok: r.ok, status: r.status, json }
}

// WAGON R5 Lot 2 (D6) — composant HoguetWarning retiré avec le bloc Génération
// IA (seul consommateur, via iaResult.hoguet_warning). Plus de bandeau Hoguet
// dans cet onglet ; la description vient de l'import plateforme, relue par l'admin.

// Preview description multi-paragraphes safe (textContent strict, F-MOY-1 + Q-D11).
function DescriptionPreview({ text, testid }) {
  if (!text) {
    return (
      <div data-testid={testid} style={{ fontSize: 12, color: '#aaa', fontStyle: 'italic' }}>
        Vide
      </div>
    )
  }
  // Split paragraphes sur \n\n (rendu p safe via React text node = textContent).
  const paras = String(text).split(/\n{2,}/).filter(Boolean)
  return (
    <div data-testid={testid} style={{ fontSize: 13, lineHeight: 1.65, color: '#333' }}>
      {paras.map((p, i) => (
        <p key={i} style={{ marginBottom: 10 }}>{p}</p>
      ))}
    </div>
  )
}

// Indicateur longueur SEO avec couleur (rouge si > max).
function SEOLengthBadge({ value, max }) {
  const len = (value || '').length
  const over = len > max
  return (
    <span style={{
      fontSize: 10, fontWeight: 600, color: over ? '#dc2626' : (len > max * 0.9 ? '#ca8a04' : DS.green),
      marginLeft: 8,
    }}>
      {len}/{max}
    </span>
  )
}

// Base URL fbz-sites Next.js (env Vercel séparé).
// Sprint 2.3 PR-D (ADR-129) : env-isé via REACT_APP_FBZ_SITES_BASE_URL bundlé
// au build CRA. Fallback hardcodé `fbz-sites.vercel.app` reste actif (alias
// Vercel maintenu post-swap DNS pour rétrocompat emails anciens).
// ⚠️ CRA : REACT_APP_* est figé au build → rebuild Vercel admin requis après
// changement env var prod (cf. dns-fullbnbzen-com-setup.md Phase 1 step RT-2).
export const FBZ_SITES_BASE_URL =
  process.env.REACT_APP_FBZ_SITES_BASE_URL || 'https://fbz-sites.vercel.app'

// ADR-197 (LOT 1) : héritage équipements retiré (chambres autonomes).
// LOT 3 : `parentLog` ré-utilisé pour calculer les SŒURS de la copie chambre→chambres.
export default function LogementWebsiteTab({ log, parentLog = null, isChambre = false, onUpdate, onUpdateBatch, onCopyToChambres }) {
  // ADR-197 LOT 3 — la section Équipements d'une CHAMBRE expose
  // « 📋 Copier vers les autres chambres ». sisters = chambres sœurs.
  // CopyToChambresButton se masque tout seul si sisters vide.
  const sisters = (isChambre && Array.isArray(parentLog?.chambres))
    ? parentLog.chambres.filter(c => c.id !== log.id)
    : []
  // ─── Import plateforme (WAGON R Lot R3 a) — move depuis SyncBeds24Tab ───
  // State + résolution du mapping Beds24 + handlers byte-identiques au Sync.
  // L'apply écrit en cfg via onUpdateBatch ; rien n'est jamais poussé vers les
  // plateformes (lecture seule). beds24_meta tracé pour audit (last_imported_at).
  const [importStatus, setImportStatus] = useState({
    state: "idle",
    error: null,
  }); // idle | loading | error | preview | success
  const [previewListing, setPreviewListing] = useState(null);
  const [previewRaw, setPreviewRaw] = useState(null);

  // Mapping Beds24 V2 (source de vérité — cf. beds24-mapping-lookup.js)
  const [mapping, setMapping] = useState(null);
  const [mappingError, setMappingError] = useState(null);
  const [mappingLoading, setMappingLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setMappingLoading(true);
    setMappingError(null);
    getBeds24Mapping(log.id)
      .then((row) => {
        if (!cancelled) {
          setMapping(row);
          setMappingLoading(false);
        }
      })
      .catch((_e) => {
        if (!cancelled) {
          setMappingError(
            "Mapping Beds24 inaccessible. Réessaie ou recharge la page.",
          );
          setMappingLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [log.id]);

  const beds24Meta = log.beds24_meta || {};

  async function handleImportFromBeds24() {
    if (!mapping) {
      setImportStatus({
        state: "error",
        error:
          "Pas de mapping Beds24 actif pour ce logement. Configure-le via Paramètres → 🔌 Beds24.",
      });
      return;
    }
    const propId = mapping.beds24_property_id;
    setPreviewListing(null);
    setPreviewRaw(null);
    setImportStatus({ state: "loading", error: null });
    try {
      const result = await fetchListingFromBeds24(
        propId,
        mapping.beds24_room_id || null,
      );
      setPreviewListing(result.listing);
      setPreviewRaw(result.raw || null);
      setImportStatus({ state: "preview", error: null });
    } catch (e) {
      setImportStatus({ state: "error", error: e.message || String(e) });
    }
  }

  function handleApplyImport(patch, fields) {
    // WAGON R5 Lot 2 (D4) — routage de l'import recentré (description + équipements) :
    //   • `description` PRÉ-REMPLIT le state FR de l'éditeur du site (descriptionFr,
    //     source logement_site_content) — JAMAIS le cfg orphelin public_description.
    //     L'admin relit/ajuste puis Enregistre (traduction auto EN/ES au save).
    //   • `equipements` (+ trace beds24_meta) écrit le cfg via onUpdateBatch (inchangé).
    const { description, ...cfgPatch } = patch || {};
    if (typeof description === "string" && description.trim().length > 0) {
      setDescriptionFr(description);
    }
    // N'écrit le cfg que s'il reste des champs cfg (equipements). Si l'admin n'a
    // coché QUE la description, on ne touche pas le cfg (et donc pas onUpdateBatch).
    const hasCfgFields = Object.keys(cfgPatch).length > 0;
    if (hasCfgFields) {
      if (!onUpdateBatch) {
        setImportStatus({
          state: "error",
          error: "onUpdateBatch indisponible (LogementsPanel non patché)",
        });
        return;
      }
      onUpdateBatch({
        ...cfgPatch,
        beds24_meta: {
          ...beds24Meta,
          last_imported_at: new Date().toISOString(),
          imported_fields: fields,
        },
      });
    }
    setPreviewListing(null);
    setPreviewRaw(null);
    setImportStatus({ state: "success", error: null });
    // Auto-clear le badge succès après 6s
    setTimeout(
      () =>
        setImportStatus((s) =>
          s.state === "success" ? { state: "idle", error: null } : s,
        ),
      6000,
    );
  }

  // ─── Équipements (ADR-197 LOT 1 — héritage parent retiré) ──────────────
  // Le SEED (LOT 0) a donné aux chambres lc1/lc2/lc3 leurs propres 14
  // équipements. Lecture SELF uniquement (plus de fallback parent ni bandeau
  // d'héritage). Le toggle agit directement sur log.equipements.
  const equipements = Array.isArray(log.equipements) ? log.equipements : [];

  function toggleEquipement(id) {
    const next = equipements.includes(id)
      ? equipements.filter((x) => x !== id)
      : [...equipements, id];
    onUpdate("equipements", next);
  }

  // ── Bootstrap state depuis logement_site_content ──────────────────────────
  // WAGON R5 Lot 2 (D5) — bouton « 🌍 Re-traduire EN/ES depuis le FR » RETIRÉ :
  // la traduction FR→EN/ES est déjà automatique au save (applySiteContentTranslations).
  // Le wrapper client handleRetranslate a été supprimé avec lui. L'action serveur
  // `site_content_retranslate_backfill` (api/sites.js) RESTE disponible via API en
  // cas de besoin ponctuel (inerte tant qu'aucun bouton ne l'appelle).

  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(null)
  // Form fields
  const [slug, setSlug] = useState('')
  const [statutPublication, setStatutPublication] = useState('draft')
  const [annonceAirbnbUrl, setAnnonceAirbnbUrl] = useState('')
  // Sprint 2.4 PR-C1 (ADR-136) — Cross-link OTA Booking.com.
  // Body key annonce_booking_url cohérent annonce_airbnb_url. Mappe colonne DB
  // `booking_url` (Phase 2.4 migration 20260627000001) via mapping serveur.
  const [annonceBookingUrl, setAnnonceBookingUrl] = useState('')
  const [descriptionFr, setDescriptionFr] = useState('')
  const [descriptionEn, setDescriptionEn] = useState('')
  const [metaSeoTitleFr, setMetaSeoTitleFr] = useState('')
  const [metaSeoDescFr, setMetaSeoDescFr] = useState('')
  const [metaSeoTitleEn, setMetaSeoTitleEn] = useState('')
  const [metaSeoDescEn, setMetaSeoDescEn] = useState('')
  // « FR = source unique » — versions ES éditables (dérivées du FR par défaut).
  const [descriptionEs, setDescriptionEs] = useState('')
  const [metaSeoTitleEs, setMetaSeoTitleEs] = useState('')
  const [metaSeoDescEs, setMetaSeoDescEs] = useState('')
  // Flags de retouche manuelle par (champ × langue), clé = nom colonne EN/ES.
  // true = retouché (figé, ne sera plus re-dérivé du FR) ; absent/false = auto.
  const [manualFlags, setManualFlags] = useState({})
  // Sprint 2.2b — champs legal/UX (Q-D1+Q-D3+Q-D4 Dimitri, mitigations B-1/B-4 pessimist).
  //   - politique_annulation : enum 4 valeurs, default 'non_remboursable' (B-1 critique).
  //   - numero_enregistrement: TEXT max 100 chars, OPTIONNEL (BUG 56), placeholder TODO_ (B-4).
  // BUG 57 — `tarif_min_nuit` retiré de l'UI : ce n'est plus une saisie admin, le
  // hero public est calculé serveur (min daily_pricing 60 j dispo). La colonne DB
  // reste dormante (plus jamais écrite par cet onglet).
  const [politiqueAnnulation, setPolitiqueAnnulation] = useState('non_remboursable')
  const [numeroEnregistrement, setNumeroEnregistrement] = useState('')
  // WAGON R5 Lot 2 (D6) — state IA (iaGenerating/iaError/iaResult) + handleGenerate
  // retirés avec le bloc « 🤖 Génération contenu IA ». La description vient
  // désormais de l'import plateforme. L'action serveur ia_generer_contenu reste.
  // Save status
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState(null)
  const [saveSuccess, setSaveSuccess] = useState(null) // 'saved' | 'published'
  // UI : toggle preview FR/EN
  const [previewLang, setPreviewLang] = useState('fr')

  // Load row existante.
  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true)
      setLoadError(null)
      try {
        const { data, error } = await supabase
          .from('logement_site_content')
          .select('*')
          .eq('logement_id', log.id)
          .maybeSingle()
        if (cancelled) return
        if (error) {
          setLoadError(error.message || 'Erreur chargement')
          setLoading(false)
          return
        }
        if (data) {
          setSlug(data.slug || '')
          setStatutPublication(data.statut_publication || 'draft')
          setAnnonceAirbnbUrl(data.annonce_airbnb_url || '')
          // Sprint 2.4 PR-C1 — booking_url colonne DB Phase 2.4.
          setAnnonceBookingUrl(data.booking_url || '')
          setDescriptionFr(data.description_fr || '')
          setDescriptionEn(data.description_en || '')
          setMetaSeoTitleFr(data.meta_seo_title_fr || '')
          setMetaSeoDescFr(data.meta_seo_description_fr || '')
          setMetaSeoTitleEn(data.meta_seo_title_en || '')
          setMetaSeoDescEn(data.meta_seo_description_en || '')
          // « FR = source unique » — versions ES + flags retouche manuelle.
          setDescriptionEs(data.description_es || '')
          setMetaSeoTitleEs(data.meta_seo_title_es || '')
          setMetaSeoDescEs(data.meta_seo_description_es || '')
          // auto_translation_hashes porte les marqueurs manual_<key>_<lang>=true.
          // On les projette sur l'état UI keyé par colonne (description_en, …).
          const hashes = (data.auto_translation_hashes && typeof data.auto_translation_hashes === 'object')
            ? data.auto_translation_hashes
            : {}
          const initialFlags = {}
          for (const f of TRANSLATABLE_KEYS) {
            if (hashes[`manual_${f.key}_en`] === true) initialFlags[f.en] = true
            if (hashes[`manual_${f.key}_es`] === true) initialFlags[f.es] = true
          }
          setManualFlags(initialFlags)
          // Sprint 2.2b — Init 3 champs legal/UX depuis row chargée
          // (mitigation B-10 pessimist : sinon useState defaults masquent
          // valeurs déjà saisies en DB → écrasement silencieux à la sauvegarde).
          setPolitiqueAnnulation(data.politique_annulation || 'non_remboursable')
          // BUG 57 — tarif_min_nuit non chargé (champ supprimé, calcul serveur).
          setNumeroEnregistrement(data.numero_enregistrement || '')
        } else {
          // Auto-suggest slug depuis nom logement (la fiche n'existe pas encore).
          setSlug(slugifyName(log.nom_complet || log.nom || ''))
        }
        setLoading(false)
      } catch (e) {
        if (cancelled) return
        setLoadError(e?.message || 'Erreur réseau')
        setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [log.id, log.nom, log.nom_complet])

  // Pré-fill slug si vide quand nom change.
  function suggestSlug() {
    setSlug(slugifyName(log.nom_complet || log.nom || ''))
  }

  // WAGON R5 Lot 2 (D6) — handleGenerate (appel ia_generer_contenu) retiré avec
  // le bloc UI « 🤖 Génération contenu IA ». La description FR est désormais
  // pré-remplie par l'import plateforme (handleApplyImport → setDescriptionFr).

  // ── « FR = source unique » — édition EN/ES + retour à l'auto ──────────────
  //
  // Toute édition d'un champ EN/ES = retouche manuelle → on pose le flag
  // (le champ ne sera plus re-dérivé du FR tant qu'on ne « revient pas à l'auto »).
  // « Revenir à l'auto » = retirer le flag + vider le champ localement ; au save
  // suivant le serveur re-traduit ce (champ, langue) depuis le FR.
  function editTranslatedField(colKey, setter, value) {
    setter(value)
    setManualFlags(prev => (prev[colKey] === true ? prev : { ...prev, [colKey]: true }))
  }
  function revertToAuto(colKey, setter) {
    setter('')
    setManualFlags(prev => {
      const next = { ...prev }
      next[colKey] = false // false = ordre explicite « revenir à l'auto » envoyé au serveur
      return next
    })
  }

  // Badge « auto » / « retouché » + bouton « revenir à la traduction auto ».
  function TranslationFieldBadge({ colKey, setter }) {
    const isManual = manualFlags[colKey] === true
    return (
      <span style={{ marginLeft: 8, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <span
          data-testid={`badge-${colKey}`}
          style={{
            fontSize: 9, fontWeight: 700, letterSpacing: 0.3, textTransform: 'uppercase',
            padding: '2px 6px', borderRadius: 6,
            background: isManual ? '#fef3c7' : '#e0f2fe',
            color: isManual ? '#92400e' : '#075985',
          }}
        >
          {isManual ? '✎ retouché' : '⟳ auto'}
        </span>
        {isManual && (
          <button
            type="button"
            onClick={() => revertToAuto(colKey, setter)}
            data-testid={`revert-${colKey}`}
            title="Annule la retouche : ce champ redeviendra une traduction automatique du FR au prochain enregistrement."
            style={{
              border: 'none', background: 'transparent', color: DS.skyDeep,
              cursor: 'pointer', fontSize: 10, fontWeight: 600, padding: 0,
              textDecoration: 'underline',
            }}
          >
            revenir à l'auto
          </button>
        )}
      </span>
    )
  }

  // ── Sprint 2.2b — Preflight publish validation (Item Z) ──────────────────
  //
  // Computed list erreurs bloquantes AVANT publish (mitigation B-10 + UX).
  // Réutilisé par :
  //   1. Tooltip + disabled state du bouton "Publier"
  //   2. Bandeau visuel dessous le bouton (publish-validation-hint)
  //   3. setSaveError defensive dans handleSave si bypass programmatique
  //
  // 6 règles bloquantes (BUG 57 : tarif min retiré — calcul serveur, plus une saisie) :
  //   - slug valide (regex)
  //   - description FR >= 50 chars
  //   - meta SEO title FR non vide
  //   - politique_annulation défini
  //
  // BUG 59 (WAGON E) — la description EN n'est PLUS une condition bloquante :
  // elle est désormais générée automatiquement (FR → EN + ES via Claude Haiku
  // au moment de la sauvegarde, gate par hash). Exiger une saisie EN manuelle
  // n'a plus de sens — le serveur garantit l'EN/ES depuis le FR.
  //
  // WAGON R5 Lot 2 (D6) — la règle « pas de hoguet_warning IA » a été retirée :
  // le bloc Génération IA (qui posait ce flag via iaResult) n'existe plus. La
  // description vient de l'import plateforme, relue par l'admin avant publication.
  const publishValidationErrors = useMemo(() => {
    const errors = []
    if (!slug || !SLUG_REGEX.test(slug)) errors.push('Slug invalide (a-z, 0-9, tirets)')
    if (!descriptionFr || descriptionFr.trim().length < 50) errors.push('Description FR < 50 chars')
    if (!metaSeoTitleFr || metaSeoTitleFr.trim().length === 0) errors.push('Titre SEO FR requis')
    // BUG 57 — « Tarif min public » n'est plus une donnée saisie (calcul serveur
    // depuis daily_pricing) : plus de règle bloquante associée à la publication.
    if (!politiqueAnnulation) errors.push('Politique annulation requise')
    return errors
  }, [slug, descriptionFr, metaSeoTitleFr, politiqueAnnulation])

  // ── Action : enregistrer (draft ou published) ─────────────────────────────
  async function handleSave(publish = false) {
    setSaveError(null)
    setSaveSuccess(null)
    if (!slug || !SLUG_REGEX.test(slug)) {
      setSaveError('Slug invalide. Caractères autorisés : a-z, 0-9, tiret. Min 2, max 80.')
      return
    }
    // Sprint 2.2b — Preflight publish (Item Z) : bloque publish si erreurs.
    // Filet de sécurité défense-en-profondeur même si bouton disabled
    // (au cas où trigger programmatique ou bypass UI).
    if (publish && publishValidationErrors.length > 0) {
      setSaveError(`Publication impossible — corriger d'abord :\n• ${publishValidationErrors.join('\n• ')}`)
      return
    }
    setSaving(true)
    try {
      const statut = publish ? 'published' : statutPublication
      const { ok, status, json } = await callApi('/api/sites?action=site_content_upsert', {
        logement_id: log.id,
        slug,
        statut_publication: statut,
        annonce_airbnb_url: annonceAirbnbUrl || null,
        // Sprint 2.4 PR-C1 (ADR-136) — body annonce_booking_url → colonne DB booking_url.
        annonce_booking_url: annonceBookingUrl || null,
        description_fr: descriptionFr,
        description_en: descriptionEn,
        meta_seo_title_fr: metaSeoTitleFr,
        meta_seo_description_fr: metaSeoDescFr,
        meta_seo_title_en: metaSeoTitleEn,
        meta_seo_description_en: metaSeoDescEn,
        // « FR = source unique » — versions ES retouchées + flags par champ.
        // manual_flags[col]=true → l'admin a retouché (figé) ; false → « revenir
        // à l'auto » (le serveur vide + re-dérive du FR). Les champs absents/auto
        // ne sont pas figés et restent dérivés du FR par applySiteContentTranslations.
        description_es: descriptionEs,
        meta_seo_title_es: metaSeoTitleEs,
        meta_seo_description_es: metaSeoDescEs,
        manual_flags: manualFlags,
        // Sprint 2.2b — champs legal/UX backend (mitigation B-5 pessimist côté API).
        // BUG 57 — tarif_min_nuit n'est plus envoyé (calcul serveur daily_pricing).
        politique_annulation: politiqueAnnulation,
        numero_enregistrement: numeroEnregistrement || null,
      })
      if (!ok) {
        const msg = json?.error === 'slug_conflict'
          ? `Slug déjà utilisé par un autre logement (${json.taken_by || 'inconnu'}).`
          : json?.error === 'invalid_slug'
            ? 'Slug invalide.'
            : json?.error === 'invalid_statut_publication'
              ? 'Statut publication invalide.'
              : json?.error === 'invalid_politique_annulation'
                ? 'Politique annulation invalide (flexible/moderee/stricte/non_remboursable).'
                : json?.error === 'invalid_tarif_min_nuit'
                  ? 'Tarif min public invalide (entier 1-9999 €).'
                  : json?.error === 'invalid_numero_enregistrement_too_long'
                    ? 'Numéro enregistrement trop long (max 100 chars).'
                    : `Erreur ${status} : ${json?.error || 'inconnue'}`
        setSaveError(msg)
        setSaving(false)
        return
      }
      setStatutPublication(json.statut_publication || statut)
      setSaveSuccess(publish ? 'published' : 'saved')
      setSaving(false)
    } catch (e) {
      setSaveError(e?.message || 'Erreur inattendue')
      setSaving(false)
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div data-testid="logement-website-tab" style={{ padding: 32, textAlign: 'center', color: '#888' }}>
        Chargement…
      </div>
    )
  }

  // Mini-fix UX : bouton "Voir le site en ligne" (visible si publié, désactivé sinon).
  const canViewSite = statutPublication === 'published' && Boolean(slug)
  // BUG 29 — route fbz-sites réelle = /[locale]/[slug]. Langue par défaut fr.
  const viewSiteUrl = canViewSite ? `${FBZ_SITES_BASE_URL}/fr/${slug}` : null
  const viewSiteTooltip = canViewSite
    ? `Ouvrir ${viewSiteUrl}`
    : "Publiez d'abord le contenu (statut Publié + slug)"

  return (
    <div data-testid="logement-website-tab">
      {/* ═════ Section 🖼️ Photos du logement (WAGON R Lot R4 b — move depuis
          Info FBZ) ═════
          Galerie centrale du logement, en haut du Website car c'est le contenu
          visuel du site. Ces photos sont consommées par le livret voyageur et
          le site public (carrousel). Composant LogementGalleryEditor inchangé
          (logement_id={log.id}). Définir une cover avec l'étoile. */}
      <Section
        title={<SectionHeading icon={Images} label="Photos du logement" />}
        intro="Galerie centrale du logement. Ces photos sont consommées par le livret voyageur et le site public (carrousel). Définis une cover avec l'étoile."
      >
        <LogementGalleryEditor logement_id={log.id} />
      </Section>

      {/* Barre d'actions haut de page */}
      <div style={{ marginBottom: 14, display: 'flex', justifyContent: 'flex-end' }}>
        <button
          type="button"
          disabled={!canViewSite}
          onClick={() => canViewSite && window.open(viewSiteUrl, '_blank', 'noopener,noreferrer')}
          title={viewSiteTooltip}
          data-testid="voir-site-en-ligne-btn"
          style={{
            background: canViewSite ? DS.skyDeep : '#cbd5e1',
            color: '#fff', border: 'none', borderRadius: 8,
            padding: '9px 16px', fontSize: 12, fontWeight: 700,
            cursor: canViewSite ? 'pointer' : 'not-allowed',
            fontFamily: DS.fontBody,
          }}
        >
          Voir le site en ligne
        </button>
      </div>

      {loadError && (
        <div style={{
          background: '#fef2f2', border: '1px solid #fca5a5', color: '#dc2626',
          padding: 12, borderRadius: 8, marginBottom: 14, fontSize: 12,
        }}>
          Erreur chargement : {loadError}
        </div>
      )}

      {/* ⚙️ Import plateforme (WAGON R Lot R3 a — move depuis 🔌 Sync Beds24).
          Bouton « Récupérer depuis Beds24 » byte-identique au Sync (styles,
          gardes, statuts). L'apply écrit en cfg via la modale de diff existante
          (onUpdateBatch) ; rien n'est jamais poussé vers les plateformes. */}
      <div
        style={{
          padding: "14px 16px",
          background: DS.cielSoft,
          borderRadius: 12,
          marginBottom: 14,
          border: `1px solid ${DS.ciel}`,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 14,
          flexWrap: "wrap",
        }}
      >
        <div style={{ flex: 1, minWidth: 240 }}>
          <div
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: DS.marine,
              fontFamily: DS.fontDisplay,
              marginBottom: 2,
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <Import size={15} strokeWidth={2} aria-hidden="true" />
            Import plateforme
          </div>
          <div style={{ fontSize: 11, color: DS.marineDeep, lineHeight: 1.5 }}>
            Pré-remplit la <strong>description</strong> du site (à relire/ajuster
            avant Enregistrer) et les <strong>équipements</strong> depuis Beds24,
            avec aperçu des différences avant application. Lecture seule — rien
            n'est jamais poussé vers les plateformes.
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {importStatus.state === "success" && (
            <span
              style={{
                background: DS.green,
                color: "#fff",
                borderRadius: 6,
                padding: "4px 10px",
                fontSize: 11,
                fontWeight: 700,
              }}
            >
              Importé
            </span>
          )}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              gap: 4,
            }}
          >
            <button
              onClick={handleImportFromBeds24}
              disabled={
                mappingLoading || !mapping || importStatus.state === "loading"
              }
              data-testid="website-import-btn"
              style={{
                background:
                  mappingLoading || !mapping || importStatus.state === "loading"
                    ? "#94a3b8"
                    : DS.navy,
                color: "#fff",
                border: "none",
                borderRadius: 8,
                padding: "10px 16px",
                fontSize: 12,
                fontWeight: 700,
                cursor:
                  mappingLoading || !mapping || importStatus.state === "loading"
                    ? "not-allowed"
                    : "pointer",
                fontFamily: DS.fontBody,
                whiteSpace: "nowrap",
                display: "inline-flex",
                alignItems: "center",
                gap: 7,
              }}
            >
              <RefreshCw size={14} strokeWidth={2.2} aria-hidden="true" />
              {importStatus.state === "loading"
                ? "Import en cours…"
                : "Récupérer maintenant"}
            </button>
            <div
              style={{
                fontSize: 11,
                color: "#666",
                marginTop: 4,
                textAlign: "right",
              }}
            >
              {mappingLoading && <>⏳ Vérification du mapping Beds24…</>}
              {!mappingLoading && mappingError && (
                <span style={{ color: DS.red }}>{mappingError}</span>
              )}
              {!mappingLoading && !mappingError && !mapping && (
                <>
                  Pas de mapping Beds24 actif pour <code>{log.id}</code>.
                  Configure-le via Paramètres → 🔌 Beds24.
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      {importStatus.state === "error" && (
        <div
          style={{
            padding: "10px 14px",
            background: "#fce8e8",
            color: DS.red,
            borderRadius: 8,
            fontSize: 12,
            marginBottom: 14,
            lineHeight: 1.5,
          }}
        >
          {importStatus.error}
        </div>
      )}

      {/* Modale preview (diff non destructif — réutilisée telle quelle) */}
      {importStatus.state === "preview" && previewListing && (
        <ImportPreviewModal
          log={log}
          listing={previewListing}
          rawData={previewRaw}
          onClose={() => {
            setPreviewListing(null);
            setPreviewRaw(null);
            setImportStatus({ state: "idle", error: null });
          }}
          onApply={handleApplyImport}
        />
      )}

      {/* ── Section ✨ Équipements (WAGON R Lot R1 — move depuis Sync Beds24) ──
          Encart visuellement distinct (correction C-2 pessimist) : le champ
          équipements écrit en CFG via onUpdate('equipements', …) — JAMAIS via
          site_content_upsert. Sauvegardé par le bouton « Enregistrer » de la
          fiche (pas par « Publier » ci-dessous). Consommateur réel = api/sites.js
          (prompt IA), intouché. JSX d'édition + helpers d'héritage byte-identiques
          (cf. SyncBeds24Tab) ; doublon temporaire assumé jusqu'au Lot R4. */}
      <div
        data-testid="website-equipements-cfg"
        style={{
          border: `1.5px solid ${DS.border}`,
          borderRadius: 12,
          padding: "4px 14px 8px",
          marginBottom: 14,
          background: DS.card,
        }}
      >
        <div
          style={{
            fontSize: 11,
            color: DS.textSecondary,
            lineHeight: 1.5,
            background: DS.cielSoft,
            border: `1px solid ${DS.border}`,
            borderRadius: 8,
            padding: "8px 12px",
            margin: "10px 0 4px",
          }}
        >
          <strong>Donnée de configuration FBZ</strong> — sauvegardée par le
          bouton Enregistrer de la fiche (pas par Publier ci-dessous). Utilisée
          par la génération IA du contenu du site.
        </div>
        <Section
          title={<SectionHeading icon={Sparkles} label="Équipements" />}
          intro="Cocher les équipements présents. Affichés sous forme de pictogrammes dans l'annonce."
        >
          <CopyToChambresButton sourceLog={log} sisters={sisters} sectionId="equipements" onCopyToChambres={onCopyToChambres} />
          {EQUIPEMENTS_GROUPS.map((group) => {
            const items = STANDARD_EQUIPEMENTS.filter((e) => e.group === group);
            return (
              <div key={group} style={{ marginBottom: 14 }}>
                <div
                  style={{
                    fontSize: 11,
                    color: "#888",
                    fontWeight: 700,
                    marginBottom: 6,
                    textTransform: "uppercase",
                    letterSpacing: 0.4,
                  }}
                >
                  {group}
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {items.map((e) => {
                    const active = equipements.includes(e.id);
                    // DA Lot L13 : picto lucide à la place de l'emoji du label.
                    const EquipIcon = EQUIP_ICONS[e.id] || EQUIP_ICON_FALLBACK;
                    return (
                      <button
                        key={e.id}
                        type="button"
                        onClick={() => toggleEquipement(e.id)}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                          padding: "6px 11px",
                          borderRadius: 8,
                          // Cochée = ciel (cielSoft + bordure ciel) ; non cochée = blanc.
                          border: `1.5px solid ${active ? DS.ciel : DS.border}`,
                          background: active ? DS.cielSoft : DS.card,
                          color: active ? DS.marine : DS.textSecondary,
                          fontWeight: active ? 700 : 500,
                          fontSize: 12,
                          cursor: "pointer",
                          fontFamily: DS.fontBody,
                        }}
                      >
                        <EquipIcon
                          size={15}
                          strokeWidth={2}
                          aria-hidden="true"
                          style={{ flexShrink: 0, color: active ? DS.marine : DS.textMuted }}
                        />
                        {active ? "✓ " : ""}
                        {equipementLabelText(e.label)}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
          <div
            style={{
              padding: "10px 12px",
              background: DS.pageBg,
              borderRadius: 7,
              fontSize: 11,
              color: "#888",
              lineHeight: 1.4,
              marginTop: 8,
            }}
          >
            Ces équipements alimentent la génération IA du contenu de votre
            site direct. Ils ne sont PAS poussés vers Airbnb / Booking (Beds24 en
            lecture seule) : les équipements y restent gérés sur chaque plateforme.{" "}
            {equipements.length} équipement{equipements.length > 1 ? "s" : ""} coché
            {equipements.length > 1 ? "s" : ""}.
          </div>
        </Section>
      </div>

      {/* ── Section Slug + statut + URL Airbnb ──────────────────────── */}
      <Section title={<SectionHeading icon={Globe} label="Slug + publication" />}>
        <Row>
          <Field label="Slug URL (a-z, 0-9, tirets, 2-80 chars)">
            <div style={{ display: 'flex', gap: 4 }}>
              <input
                type="text"
                value={slug ?? ''}
                onChange={e => setSlug(e.target.value)}
                placeholder="cocon-naturel-test"
                data-testid="slug-input"
                style={{
                  border: '1px solid #dde3ec', borderRadius: 7, padding: '7px 10px',
                  fontSize: 12, outline: 'none', background: DS.pageBg,
                  fontFamily: DS.fontBody, width: '100%',
                }}
              />
              <button
                type="button"
                onClick={suggestSlug}
                title="Auto-suggérer depuis nom logement"
                style={{
                  border: 'none', background: DS.skyDeep, color: '#fff',
                  borderRadius: 7, padding: '0 10px', cursor: 'pointer', fontSize: 11,
                }}
              >
                ↺ Auto
              </button>
            </div>
          </Field>
          <Field label="Statut publication">
            {/* DA Lot L13 — segmenté (Brouillon / Publié vert / Archivé). Même
                état `statutPublication` et même setter que l'ancien Select :
                aucune logique ni valeur d'enum modifiée (display seul). */}
            <div
              role="group"
              aria-label="Statut publication"
              style={{
                display: "inline-flex",
                flexWrap: "wrap",
                gap: 4,
                background: DS.cielSoft,
                border: `1px solid ${DS.border}`,
                borderRadius: 9,
                padding: 3,
              }}
            >
              {[
                { value: "draft", label: "Brouillon", activeBg: DS.marine },
                { value: "published", label: "Publié", activeBg: DS.success },
                { value: "archived", label: "Archivé", activeBg: DS.textSecondary },
              ].map((opt) => {
                const on = statutPublication === opt.value
                return (
                  <button
                    key={opt.value}
                    type="button"
                    aria-pressed={on}
                    onClick={() => setStatutPublication(opt.value)}
                    style={{
                      border: "none",
                      borderRadius: 7,
                      padding: "6px 14px",
                      fontSize: 12,
                      fontWeight: 700,
                      cursor: "pointer",
                      fontFamily: DS.fontBody,
                      background: on ? opt.activeBg : "transparent",
                      color: on ? "#fff" : DS.textSecondary,
                    }}
                  >
                    {opt.label}
                  </button>
                )
              })}
            </div>
          </Field>
        </Row>
        <FieldFull label="URL Airbnb équivalent (CTA voyageur de repli)">
          <input
            type="text"
            value={annonceAirbnbUrl ?? ''}
            onChange={e => setAnnonceAirbnbUrl(e.target.value)}
            placeholder="https://www.airbnb.fr/rooms/123456789"
            data-testid="airbnb-url-input"
            style={{
              border: '1px solid #dde3ec', borderRadius: 7, padding: '7px 10px',
              fontSize: 12, outline: 'none', background: DS.pageBg,
              fontFamily: DS.fontBody, width: '100%',
            }}
          />
        </FieldFull>
        {/* Sprint 2.4 PR-C1 (ADR-136) — URL Booking.com cross-link voyageur.
            Affiché côté fbz-sites dans la section "Aussi disponible sur" entre
            BookingForm et footer Hoguet (Q-D-C1-1 architect placement). */}
        <FieldFull label="URL Booking.com équivalent (cross-link voyageur)">
          <input
            type="text"
            value={annonceBookingUrl ?? ''}
            onChange={e => setAnnonceBookingUrl(e.target.value)}
            placeholder="https://www.booking.com/hotel/fr/votre-listing.html"
            data-testid="booking-url-input"
            style={{
              border: '1px solid #dde3ec', borderRadius: 7, padding: '7px 10px',
              fontSize: 12, outline: 'none', background: DS.pageBg,
              fontFamily: DS.fontBody, width: '100%',
            }}
          />
        </FieldFull>
      </Section>

      {/* ── Section Sprint 2.2b — Champs légaux + commerce ──────────────── */}
      <Section
        title={<SectionHeading icon={Scale} label="Mentions légales + commerce (Sprint 2.2)" bg={DS.catArdoiseBg} color={DS.catArdoiseIco} />}
        intro="Politique d'annulation visible voyageur. Numéro d'enregistrement OPTIONNEL : s'il est renseigné, il s'affiche au footer du site public (art. L324-1-1 CT) ; sinon rien n'est affiché. Le tarif « À partir de X €/nuit » du hero est calculé automatiquement depuis les disponibilités."
      >
        <Row>
          <Field label="Politique d'annulation (visible voyageur)">
            <Select
              value={politiqueAnnulation}
              onChange={setPolitiqueAnnulation}
              options={[
                { value: 'non_remboursable', label: 'Non remboursable (par défaut FBZ)' },
                { value: 'stricte', label: 'Stricte (50% si annulé > 30j avant)' },
                { value: 'moderee', label: 'Modérée (50% si annulé > 5j avant)' },
                { value: 'flexible', label: 'Flexible (100% si annulé > 48h avant)' },
              ]}
            />
          </Field>
        </Row>
        {/* BUG 57 — champ « Tarif min public » SUPPRIMÉ. Le hero « À partir de
            X €/nuit » est désormais calculé côté serveur (api/sites.js) :
            min des prix des 60 prochains jours DISPONIBLES depuis daily_pricing.
            Aucune saisie admin. Si aucune donnée → le tarif n'est pas affiché. */}
        <div style={{ fontSize: 11, color: '#666', marginTop: -4, marginBottom: 12 }}>
          Le tarif « À partir de X €/nuit » affiché sur la page publique est calculé
          automatiquement à partir des disponibilités (60 prochains jours). Aucune saisie ici.
        </div>
        <FieldFull label="Numéro enregistrement meublé tourisme (mairie Toulouse)">
          <input
            type="text"
            value={numeroEnregistrement ?? ''}
            onChange={e => setNumeroEnregistrement(e.target.value)}
            placeholder="Ex: 13EHGW (laisser TODO_NUMERO_ENREGISTREMENT_xx si pas encore reçu)"
            maxLength="100"
            data-testid="numero-enregistrement-input"
            style={{
              border: '1px solid #dde3ec', borderRadius: 7, padding: '7px 10px',
              fontSize: 12, outline: 'none', background: DS.pageBg,
              fontFamily: DS.fontBody, width: '100%',
            }}
          />
        </FieldFull>
        <div style={{ fontSize: 11, color: '#666', marginTop: -4 }}>
          Optionnel. S'il est renseigné, il s'affiche au footer du site public
          (art. L324-1-1 Code du Tourisme) ; sinon rien n'est affiché. La publication
          n'est pas bloquée s'il est vide.
        </div>
      </Section>

      {/* WAGON R5 Lot 2 (D6) — bloc « 🤖 Génération contenu IA » RETIRÉ : la
          description est désormais pré-remplie via l'import plateforme (Beds24),
          relue/ajustée puis enregistrée (traduction auto EN/ES au save). L'action
          serveur `ia_generer_contenu` (api/sites.js) RESTE disponible via API. */}

      {/* ── Section Preview — 3 onglets FR | EN | ES, chacun éditable ──── */}
      <Section
        title={<SectionHeading icon={FileText} label="Aperçu contenu" />}
        intro="Le FR est l'unique source. EN et ES sont traduits automatiquement à l'enregistrement. Toute retouche EN/ES est conservée (badge « retouché ») jusqu'à « revenir à l'auto »."
      >
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          {[
            { code: 'fr', label: '🇫🇷 FR' },
            { code: 'en', label: '🇬🇧 EN' },
            { code: 'es', label: '🇪🇸 ES' },
          ].map(({ code, label }) => (
            <button
              key={code}
              type="button"
              onClick={() => setPreviewLang(code)}
              data-testid={`preview-tab-${code}`}
              style={{
                border: 'none',
                background: previewLang === code ? DS.navy : DS.cielSoft,
                color: previewLang === code ? '#fff' : '#444',
                padding: '6px 14px', borderRadius: 7, cursor: 'pointer',
                fontSize: 12, fontWeight: 700,
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {previewLang === 'fr' ? (
          <div>
            <FieldFull label={<>Description FR <span style={{ fontSize: 10, color: '#888', fontWeight: 400 }}>(édition libre + auto-overwrite si IA)</span></>}>
              <textarea
                value={descriptionFr}
                onChange={e => setDescriptionFr(e.target.value)}
                rows={8}
                placeholder="Description publique du logement…"
                data-testid="description-fr-input"
                style={{
                  width: '100%', border: '1px solid #dde3ec', borderRadius: 7,
                  padding: '8px 10px', fontSize: 12, fontFamily: DS.fontBody,
                  resize: 'vertical', background: DS.pageBg, lineHeight: 1.5,
                }}
              />
            </FieldFull>
            <Row>
              <Field label={<>Meta title FR <SEOLengthBadge value={metaSeoTitleFr} max={SEO_TITLE_MAX} /></>}>
                <input
                  type="text"
                  value={metaSeoTitleFr ?? ''}
                  onChange={e => setMetaSeoTitleFr(e.target.value)}
                  placeholder="Titre SEO 50-60 chars"
                  data-testid="meta-title-fr-input"
                  style={{
                    border: '1px solid #dde3ec', borderRadius: 7, padding: '7px 10px',
                    fontSize: 12, outline: 'none', background: DS.pageBg,
                    fontFamily: DS.fontBody, width: '100%',
                  }}
                />
              </Field>
              <Field label={<>Meta description FR <SEOLengthBadge value={metaSeoDescFr} max={SEO_DESC_MAX} /></>}>
                <input
                  type="text"
                  value={metaSeoDescFr ?? ''}
                  onChange={e => setMetaSeoDescFr(e.target.value)}
                  placeholder="Description SEO 140-160 chars"
                  data-testid="meta-desc-fr-input"
                  style={{
                    border: '1px solid #dde3ec', borderRadius: 7, padding: '7px 10px',
                    fontSize: 12, outline: 'none', background: DS.pageBg,
                    fontFamily: DS.fontBody, width: '100%',
                  }}
                />
              </Field>
            </Row>
            <div style={{ marginTop: 12, padding: 12, background: DS.pageBg, borderRadius: 8 }}>
              <div style={{ fontSize: 11, color: DS.textMuted, fontWeight: 700, marginBottom: 8, display: 'inline-flex', alignItems: 'center', gap: 6 }}><Eye size={13} strokeWidth={2} aria-hidden="true" />Aperçu rendu FR</div>
              <DescriptionPreview text={descriptionFr} testid="ia-preview-fr" />
            </div>
          </div>
        ) : previewLang === 'en' ? (
          <div>
            <FieldFull label={<>Description EN <TranslationFieldBadge colKey="description_en" setter={setDescriptionEn} /> <span style={{ fontSize: 10, color: '#888', fontWeight: 400 }}>(traduite auto FR→EN à l'enregistrement ; retouche conservée jusqu'à « revenir à l'auto »)</span></>}>
              <textarea
                value={descriptionEn}
                onChange={e => editTranslatedField('description_en', setDescriptionEn, e.target.value)}
                rows={8}
                placeholder="Auto-traduit depuis le FR à l'enregistrement…"
                data-testid="description-en-input"
                style={{
                  width: '100%', border: '1px solid #dde3ec', borderRadius: 7,
                  padding: '8px 10px', fontSize: 12, fontFamily: DS.fontBody,
                  resize: 'vertical', background: DS.pageBg, lineHeight: 1.5,
                }}
              />
            </FieldFull>
            <Row>
              <Field label={<>Meta title EN <SEOLengthBadge value={metaSeoTitleEn} max={SEO_TITLE_MAX} /> <TranslationFieldBadge colKey="meta_seo_title_en" setter={setMetaSeoTitleEn} /></>}>
                <input
                  type="text"
                  value={metaSeoTitleEn ?? ''}
                  onChange={e => editTranslatedField('meta_seo_title_en', setMetaSeoTitleEn, e.target.value)}
                  placeholder="SEO title 50-60 chars"
                  data-testid="meta-title-en-input"
                  style={{
                    border: '1px solid #dde3ec', borderRadius: 7, padding: '7px 10px',
                    fontSize: 12, outline: 'none', background: DS.pageBg,
                    fontFamily: DS.fontBody, width: '100%',
                  }}
                />
              </Field>
              <Field label={<>Meta description EN <SEOLengthBadge value={metaSeoDescEn} max={SEO_DESC_MAX} /> <TranslationFieldBadge colKey="meta_seo_description_en" setter={setMetaSeoDescEn} /></>}>
                <input
                  type="text"
                  value={metaSeoDescEn ?? ''}
                  onChange={e => editTranslatedField('meta_seo_description_en', setMetaSeoDescEn, e.target.value)}
                  placeholder="SEO description 140-160 chars"
                  data-testid="meta-desc-en-input"
                  style={{
                    border: '1px solid #dde3ec', borderRadius: 7, padding: '7px 10px',
                    fontSize: 12, outline: 'none', background: DS.pageBg,
                    fontFamily: DS.fontBody, width: '100%',
                  }}
                />
              </Field>
            </Row>
            <div style={{ marginTop: 12, padding: 12, background: DS.pageBg, borderRadius: 8 }}>
              <div style={{ fontSize: 11, color: DS.textMuted, fontWeight: 700, marginBottom: 8, display: 'inline-flex', alignItems: 'center', gap: 6 }}><Eye size={13} strokeWidth={2} aria-hidden="true" />Aperçu rendu EN</div>
              <DescriptionPreview text={descriptionEn} testid="ia-preview-en" />
            </div>
          </div>
        ) : (
          <div>
            <FieldFull label={<>Description ES <TranslationFieldBadge colKey="description_es" setter={setDescriptionEs} /> <span style={{ fontSize: 10, color: '#888', fontWeight: 400 }}>(traduite auto FR→ES à l'enregistrement ; retouche conservée jusqu'à « revenir à l'auto »)</span></>}>
              <textarea
                value={descriptionEs}
                onChange={e => editTranslatedField('description_es', setDescriptionEs, e.target.value)}
                rows={8}
                placeholder="Auto-traducido desde el FR al guardar…"
                data-testid="description-es-input"
                style={{
                  width: '100%', border: '1px solid #dde3ec', borderRadius: 7,
                  padding: '8px 10px', fontSize: 12, fontFamily: DS.fontBody,
                  resize: 'vertical', background: DS.pageBg, lineHeight: 1.5,
                }}
              />
            </FieldFull>
            <Row>
              <Field label={<>Meta title ES <SEOLengthBadge value={metaSeoTitleEs} max={SEO_TITLE_MAX} /> <TranslationFieldBadge colKey="meta_seo_title_es" setter={setMetaSeoTitleEs} /></>}>
                <input
                  type="text"
                  value={metaSeoTitleEs ?? ''}
                  onChange={e => editTranslatedField('meta_seo_title_es', setMetaSeoTitleEs, e.target.value)}
                  placeholder="Título SEO 50-60 caracteres"
                  data-testid="meta-title-es-input"
                  style={{
                    border: '1px solid #dde3ec', borderRadius: 7, padding: '7px 10px',
                    fontSize: 12, outline: 'none', background: DS.pageBg,
                    fontFamily: DS.fontBody, width: '100%',
                  }}
                />
              </Field>
              <Field label={<>Meta description ES <SEOLengthBadge value={metaSeoDescEs} max={SEO_DESC_MAX} /> <TranslationFieldBadge colKey="meta_seo_description_es" setter={setMetaSeoDescEs} /></>}>
                <input
                  type="text"
                  value={metaSeoDescEs ?? ''}
                  onChange={e => editTranslatedField('meta_seo_description_es', setMetaSeoDescEs, e.target.value)}
                  placeholder="Descripción SEO 140-160 caracteres"
                  data-testid="meta-desc-es-input"
                  style={{
                    border: '1px solid #dde3ec', borderRadius: 7, padding: '7px 10px',
                    fontSize: 12, outline: 'none', background: DS.pageBg,
                    fontFamily: DS.fontBody, width: '100%',
                  }}
                />
              </Field>
            </Row>
            <div style={{ marginTop: 12, padding: 12, background: DS.pageBg, borderRadius: 8 }}>
              <div style={{ fontSize: 11, color: DS.textMuted, fontWeight: 700, marginBottom: 8, display: 'inline-flex', alignItems: 'center', gap: 6 }}><Eye size={13} strokeWidth={2} aria-hidden="true" />Aperçu rendu ES</div>
              <DescriptionPreview text={descriptionEs} testid="ia-preview-es" />
            </div>
          </div>
        )}
      </Section>

      {/* ── Section actions enregistrer / publier ──────────────────── */}
      <Section title={<SectionHeading icon={Save} label="Actions" bg={DS.catArdoiseBg} color={DS.catArdoiseIco} />}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {/* DA Lot L13 — Enregistrer blanc (bordure) / Publier corail. */}
          <button
            type="button"
            onClick={() => handleSave(false)}
            disabled={saving}
            data-testid="save-btn"
            style={{
              background: DS.card,
              color: saving ? DS.textMuted : DS.marine,
              border: `1.5px solid ${saving ? DS.border : DS.borderHover}`,
              borderRadius: 9, padding: '9px 18px',
              fontSize: 13, fontWeight: 700, cursor: saving ? 'wait' : 'pointer',
              fontFamily: DS.fontBody,
              display: 'inline-flex', alignItems: 'center', gap: 8,
            }}
          >
            <Save size={15} strokeWidth={2} aria-hidden="true" />
            Enregistrer (brouillon)
          </button>
          <button
            type="button"
            onClick={() => handleSave(true)}
            disabled={saving || publishValidationErrors.length > 0}
            data-testid="publish-btn"
            title={
              publishValidationErrors.length > 0
                ? `Bloquant : ${publishValidationErrors.join(' • ')}`
                : 'Publier le contenu en ligne'
            }
            style={{
              background: (saving || publishValidationErrors.length > 0) ? '#cbd5e1' : DS.corail,
              color: '#fff',
              border: 'none', borderRadius: 9, padding: '10px 18px',
              fontSize: 13, fontWeight: 700,
              cursor: (saving || publishValidationErrors.length > 0) ? 'not-allowed' : 'pointer',
              fontFamily: DS.fontBody,
              display: 'inline-flex', alignItems: 'center', gap: 8,
            }}
          >
            <Globe size={15} strokeWidth={2} aria-hidden="true" />
            Publier
          </button>
        </div>
        {publishValidationErrors.length > 0 && (
          <div data-testid="publish-validation-hint" style={{
            background: '#fffbeb', border: '1px solid #fcd34d', color: '#92400e',
            padding: 10, borderRadius: 8, marginTop: 12, fontSize: 12,
          }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>Publication bloquée — corriger d'abord :</div>
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {publishValidationErrors.map((err, i) => (
                <li key={i}>{err}</li>
              ))}
            </ul>
          </div>
        )}
        {saveError && (
          <div style={{
            background: '#fef2f2', border: '1px solid #fca5a5', color: '#dc2626',
            padding: 10, borderRadius: 8, marginTop: 12, fontSize: 12,
          }}>
            {saveError}
          </div>
        )}
        {saveSuccess && (
          <div data-testid="save-success" style={{
            background: '#eef7ee', border: '1px solid #86efac', color: DS.green,
            padding: 10, borderRadius: 8, marginTop: 12, fontSize: 12, fontWeight: 600,
          }}>
            {saveSuccess === 'published' ? 'Publié — revalidate fbz-sites déclenché' : 'Brouillon enregistré'}
          </div>
        )}
      </Section>
    </div>
  )
}
